import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../../redux/store";

import { Button, Header, LoginModal, Title, Breadcrumbs } from "../../../components";
import { setTempProfileData, updateTempProfileField, ITempProfileData } from "../../../redux/profile/tempProfiles/slice";
import { CalendarModal } from "../UserListProfile/components/CalendarModal";
import {
  FamilyView,
  GiftView,
  OtherView,
  ValidationError,
} from "../UserListProfile/components";
import {
  useProfileValidation,
  useSubmitHandler,
  useCalendarState,
} from "../UserListProfile/hooks";
import { IProfile } from "../UserListProfile/hooks/useProfileData";

import { subscriptionsSelector } from "../../../redux/subscriptions/selectors";
import { useAppDispatch } from "../../../redux/store";
import { isAuthenticatedSelector } from "../../../redux/profile/authAndReg/selectors";
import { setShowWindow } from "../../../redux/profile/authAndReg/slice";
import { IBasketTempItem } from "../../../redux/cart/basketTemp/slice";
import {
  resetSubscrFormsData,
  setSelectedAdultSubscr,
  setSelectedFamilySubscription,
  setSelectedKidSubscr,
} from "../../../redux/subscriptions/slice";

import styles from "./SubscriptionsForms.module.scss";

import { WindowNames } from "../../../resources/constants";
import {
  getSelectedAdultSubscrFromStorage,
  getSelectedFamilySubscrFromStorage,
  getSelectedKidSubscrFromStorage,
} from "../../../utils";

import { SubscrFormsPropsType } from "./types";
import {
  ISelectedAdultSubscr,
  ISelectedFamilySubscr,
  ISelectedKidSubscr,
} from "../../../redux/subscriptions/types";

export const SubscriptionsForms: React.FC<SubscrFormsPropsType> = ({ title }) => {
  const [isOpenModalNoAuth, setIsOpenModalNoAuth] = React.useState(false);

  const { selectedAdultSubscr, selectedKidSubscr, selectedFamilySubscr } =
    useSelector(subscriptionsSelector);
  const tempProfilesData = useSelector((state: RootState) => state.tempProfiles.tempProfilesData);
  const auth = useSelector(isAuthenticatedSelector);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const location = useLocation();

  const giftSubscrArray = selectedAdultSubscr.filter(
    (item) => item.subscription.isGift || (item.subscription as any).type === "gift"
  );
  const regularAdultSubscrArray = selectedAdultSubscr.filter(
    (item) => !item.subscription.isGift && (item.subscription as any).type !== "gift"
  );

  const allProfiles = React.useMemo(() => {
    const profiles: IProfile[] = [];

    regularAdultSubscrArray.forEach((item) => {
      for (let i = 0; i < item.quantity; i++) {
        profiles.push({
          id: `${item.subscription.id}-${i}`,
          type: (item.subscription as any).type === "daily" ? "daily" : "adult",
          subscriptionId: item.subscription.id,
          subscriptionTitle: item.subscription.title,
          price: item.subscription.price,
        });
      }
    });

    selectedKidSubscr.forEach((item) => {
      for (let i = 0; i < item.quantity; i++) {
        profiles.push({
          id: `${item.subscription.id}-${i}`,
          type: "child",
          subscriptionId: item.subscription.id,
          subscriptionTitle: item.subscription.title,
          price: item.subscription.price,
        });
      }
    });

    selectedFamilySubscr.forEach((item) => {
      const adultsCount = item.profilesCount?.adult || 0;
      const childrenCount = item.profilesCount?.kid || 0;

      for (let i = 0; i < adultsCount; i++) {
        profiles.push({
          id: `${item.subscription.id}-adult-${i}`,
          type: "adult",
          subscriptionId: item.subscription.id,
          subscriptionTitle: item.subscription.title,
          price: item.subscription.price,
        });
      }

      for (let i = 0; i < childrenCount; i++) {
        profiles.push({
          id: `${item.subscription.id}-child-${i}`,
          type: "child",
          subscriptionId: item.subscription.id,
          subscriptionTitle: item.subscription.title,
          price: item.subscription.price,
        });
      }
    });

    return profiles;
  }, [regularAdultSubscrArray, selectedKidSubscr, selectedFamilySubscr]);

  const hasFamily = selectedFamilySubscr.length > 0;
  const hasGift = giftSubscrArray.length > 0;

  const { deliveryDate, setDeliveryDate, showCalendar, setShowCalendar } = useCalendarState();
  const { validateProfile, validateAllProfiles, validateGiftProfile } = useProfileValidation(
    tempProfilesData,
    allProfiles,
    hasGift,
    deliveryDate
  );

  const basketTempForSubmit = React.useMemo(() => {
    const basketTempItems: IBasketTempItem[] = [];
    const subscriptionMap = new Map<string | number, IBasketTempItem>();

    allProfiles.forEach((profile) => {
      const subscriptionId = profile.subscriptionId;
      const subscriptionType = profile.type;

      if (!subscriptionMap.has(subscriptionId)) {
        const subscription =
          regularAdultSubscrArray.find((item) => item.subscription.id === subscriptionId)?.subscription ||
          selectedKidSubscr.find((item) => item.subscription.id === subscriptionId)?.subscription ||
          selectedFamilySubscr.find((item) => item.subscription.id === subscriptionId)?.subscription;

        if (subscription) {
          const basketItem: IBasketTempItem = {
            id: subscriptionId,
            type: (subscription.type === "kid" ? "child" : subscription.type) as any,
            title: subscription.title,
            price: subscription.price,
            profiles: [],
          };

          if (subscription.type === "family") {
            const familyItem = selectedFamilySubscr.find((item) => item.subscription.id === subscriptionId);
            if (familyItem?.profilesCount) {
              basketItem.adultsCount = familyItem.profilesCount.adult;
              basketItem.childrenCount = familyItem.profilesCount.kid;
              basketItem.totalPeople = (familyItem.profilesCount.adult || 0) + (familyItem.profilesCount.kid || 0);
            }
          }

          subscriptionMap.set(subscriptionId, basketItem);
        }
      }

      const basketItem = subscriptionMap.get(subscriptionId);
      if (basketItem) {
        const profileTypeForBasket: "adult" | "child" | "daily" =
          subscriptionType === "child" ? "child" : subscriptionType === "daily" ? "daily" : "adult";

        basketItem.profiles = basketItem.profiles || [];
        basketItem.profiles.push({
          id: profile.id,
          type: profileTypeForBasket,
        });
      }
    });

    if (hasGift) {
      giftSubscrArray.forEach((item) => {
        const giftItem: IBasketTempItem = {
          id: item.subscription.id,
          type: "gift" as any,
          title: item.subscription.title,
          price: item.subscription.price,
          profiles: [
            { id: "sender", type: "adult" },
            { id: "receiver", type: "adult" },
          ],
        };
        basketTempItems.push(giftItem);
      });
    }

    basketTempItems.push(...Array.from(subscriptionMap.values()));
    return basketTempItems;
  }, [allProfiles, regularAdultSubscrArray, selectedKidSubscr, selectedFamilySubscr, giftSubscrArray, hasGift]);

  const { handleSubmit } = useSubmitHandler(
    basketTempForSubmit,
    tempProfilesData,
    validateAllProfiles,
    deliveryDate,
    hasGift
  );

  const [validateError, setValidateError] = useState(false);
  const [submitError, setSubmitError] = useState("");

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const [dataLoaded, setDataLoaded] = React.useState(false);

  React.useEffect(() => {
    if (dataLoaded) {
      return;
    }

    const selectedAdultSubscr = getSelectedAdultSubscrFromStorage();
    const selectedKidSubscr = getSelectedKidSubscrFromStorage();
    const selectedFamilySubscr = getSelectedFamilySubscrFromStorage();

    if (selectedAdultSubscr) {
      selectedAdultSubscr.forEach((item: ISelectedAdultSubscr) =>
        dispatch(setSelectedAdultSubscr(item))
      );
    }
    if (selectedKidSubscr) {
      selectedKidSubscr.forEach((item: ISelectedKidSubscr) =>
        dispatch(setSelectedKidSubscr(item))
      );
    }
    if (selectedFamilySubscr) {
      selectedFamilySubscr.forEach((item: ISelectedFamilySubscr) =>
        dispatch(setSelectedFamilySubscription(item))
      );
    }

    setDataLoaded(true);
    // eslint-disable-next-line
  }, []);

  const updateProfile = React.useCallback(
    (profileId: string, field: keyof ITempProfileData, value: string) => {
      dispatch(updateTempProfileField({ profileId, field, value }));
      if (validateError) {
        setValidateError(false);
      }
    },
    [dispatch, validateError, setValidateError]
  );

  useEffect(() => {
    if (allProfiles.length > 0) {
      allProfiles.forEach((profile) => {
        if (!tempProfilesData[profile.id]) {
          dispatch(
            setTempProfileData({
              profileId: profile.id,
              profileData: {
                name: "",
                phone: "",
                email: "",
                birthDate: "",
                message: "",
              },
            })
          );
        }
      });
    }
  }, [allProfiles, tempProfilesData, dispatch]);

  useEffect(() => {
    if (hasGift) {
      if (!tempProfilesData["sender"]) {
        dispatch(
          setTempProfileData({
            profileId: "sender",
            profileData: {
              name: "",
              phone: "",
              email: "",
              birthDate: "",
              message: "",
            },
          })
        );
      }
      if (!tempProfilesData["receiver"]) {
        dispatch(
          setTempProfileData({
            profileId: "receiver",
            profileData: {
              name: "",
              phone: "",
              email: "",
              birthDate: "",
              message: "",
            },
          })
        );
      }
    }
  }, [hasGift, tempProfilesData, dispatch]);

  useEffect(() => {
    if (validateAllProfiles) {
      setValidateError(false);
    }
  }, [validateAllProfiles]);

  const handleSubmitClick = async () => {
    if (!validateAllProfiles) {
      setValidateError(true);
      return;
    }

    if (!auth) {
      setIsOpenModalNoAuth(true);
      dispatch(setShowWindow(WindowNames.AUTH));
      return;
    }

    setValidateError(false);
    setSubmitError("");

    try {
      await handleSubmit();

      dispatch(resetSubscrFormsData());
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Не удалось выполнить запрос. Попробуйте позже.";
      setSubmitError(errorMessage);
    }
  };

  const renderContent = () => {
    if (hasFamily) {
      return (
        <FamilyView
          basketTemp={basketTempForSubmit}
          allProfiles={allProfiles}
          tempProfilesData={tempProfilesData}
          updateProfile={updateProfile}
          validateProfile={validateProfile}
        />
      );
    } else if (hasGift) {
      return (
        <GiftView
          basketTemp={basketTempForSubmit}
          tempProfilesData={tempProfilesData}
          updateProfile={updateProfile}
          deliveryDate={deliveryDate}
          setShowCalendar={setShowCalendar}
          validateProfile={validateGiftProfile}
        />
      );
    } else {
      return (
        <OtherView
          allProfiles={allProfiles}
          tempProfilesData={tempProfilesData}
          updateProfile={updateProfile}
          validateProfile={validateProfile}
        />
      );
    }
  };

  const hasSelectedSubscriptions =
    selectedAdultSubscr.length > 0 ||
    selectedKidSubscr.length > 0 ||
    selectedFamilySubscr.length > 0;

  return (
    <>
      <Header title={title} />
      <main>
        <div className="container">
          <Breadcrumbs />
        </div>
        <section className="container">
          <Title>{title}</Title>
          <div className={styles.content}>
            {hasSelectedSubscriptions && renderContent()}

            {hasSelectedSubscriptions && (
              <>
                <ValidationError validateError={validateError} submitError={submitError} />
                <Button
                  onClickFunc={handleSubmitClick}
                  disabled={!validateAllProfiles}
                  newStyles={styles.submitButton}
                >
                  {hasGift ? "Оплатить" : "Добавить в корзину"}
                </Button>
              </>
            )}
          </div>

          <CalendarModal
            visible={showCalendar}
            onClose={() => setShowCalendar(false)}
            onSelectDate={setDeliveryDate}
            selectedDate={deliveryDate}
          />
        </section>
      </main>

      {isOpenModalNoAuth ? (
        <LoginModal isOpen={isOpenModalNoAuth} setIsOpen={setIsOpenModalNoAuth} />
      ) : null}
    </>
  );
};
